/**
 * Pure path helpers for the two settings trees.
 *
 * Settings are tenant-scoped like the rest of the app: the canonical URLs
 * are `/$org/settings/$section` (Linear-style), while the legacy org-less
 * `/settings/*` tree survives as a redirector — and as the real tree on
 * servers with no tenant directory (plain T3 dev, org-less sessions).
 *
 * Everything here is derived from `SettingsPath`, so a new settings page
 * added to the catalog picks up both trees automatically.
 *
 * @module components/settings/settingsPaths
 */
import type { SettingsPath } from "./settingsSearch";

/** "account" | "general" | … — the `$section` param, derived from the catalog. */
export type SettingsSectionSlug = SettingsPath extends `/settings/${infer S}` ? S : never;

/** "/settings/general" → "general". */
export function settingsSectionSlug(path: SettingsPath): SettingsSectionSlug {
  return path.slice("/settings/".length) as SettingsSectionSlug;
}

/**
 * The section a settings pathname points at, in either tree, or null for
 * non-settings pathnames. `/settings` and `/$org/settings` map to "" —
 * callers treat that as "general".
 */
export function settingsSectionFromPathname(pathname: string): string | null {
  const match = /^(?:\/[^/]+)?\/settings(?:\/(.*))?$/.exec(pathname);
  return match ? (match[1] ?? "") : null;
}

/** True on both settings trees; the chrome swaps in the settings sidebar. */
export function isSettingsPathname(pathname: string): boolean {
  return settingsSectionFromPathname(pathname) !== null;
}
